import { useEffect, useState, useCallback, useRef } from "react";
import { ALERT_CONFIG } from "../../../helpers/config";

export const AlertConfirmModal = (props) => {
  const {
    title = "Apakah anda yakin?",
    message,
    type = "warning",
    iconConfirm = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-trash">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M4 7l16 0" />
        <path d="M10 11l0 6" />
        <path d="M14 11l0 6" />
        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
      </svg>
    ),

    iconCancel: _iconCancel = "",
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    requireReason = false,
    reasonLabel = "Alasan",
    reasonPlaceholder = "Tuliskan alasan...",
    reasonHint = "Alasan akan disimpan pada histori transaksi.",
    waitForConfirm = false,
    idModal = "alert",
    idData,
  } = props;
  const modalRef = useRef(null);
  const pendingCallbackRef = useRef(null);
  const isConfirmCloseRef = useRef(false);

  const [show, setShow] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const config = ALERT_CONFIG[type];

  const handleKeydown = useCallback(
    (_event) => {
      if (!show) return;

      // if (event.key === "Enter") {
      //   handleConfirm(idData);
      // }
    },
    [show],
  );

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDownWrapper = (event) => {
      handleKeydown(event);
    };

    document.addEventListener("keydown", handleKeyDownWrapper);

    return () => {
      document.removeEventListener("keydown", handleKeyDownWrapper);
    };
  }, [handleKeydown]);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    const handleShowModal = () => setShow(true);
    const handleHiddenModal = () => {
      setShow(false);
      setReason("");
      setError("");
      setIsSubmitting(false);

      const callback = pendingCallbackRef.current;
      pendingCallbackRef.current = null;
      isConfirmCloseRef.current = false;
      callback?.();
    };

    modalElement.addEventListener("show.bs.modal", handleShowModal);
    modalElement.addEventListener("hidden.bs.modal", handleHiddenModal);

    // Cleanup
    return () => {
      modalElement.removeEventListener("show.bs.modal", handleShowModal);
      modalElement.removeEventListener("hidden.bs.modal", handleHiddenModal);
      pendingCallbackRef.current = null;
      isConfirmCloseRef.current = false;
    };
  }, []);

  const prepareClose = useCallback((callback) => {
    pendingCallbackRef.current = callback || null;
  }, []);

  const closeAfterValidation = useCallback(
    (callback) => {
      isConfirmCloseRef.current = true;
      prepareClose(callback);

      const buttonClose = modalRef.current?.querySelector(".btn-close");
      if (buttonClose) {
        buttonClose.click();
        return;
      }

      pendingCallbackRef.current = null;
      isConfirmCloseRef.current = false;
      callback?.();
    },
    [prepareClose],
  );

  const handleConfirm = useCallback(
    async (id) => {
      if (!show) return;

      if (requireReason && !reason.trim()) {
        setError("Alasan wajib diisi.");
        return;
      }

      setError("");
      if (waitForConfirm) {
        setIsSubmitting(true);
        try {
          const result = await onConfirm?.(id, reason.trim());
          if (result !== false) closeAfterValidation();
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      closeAfterValidation(() => {
        onConfirm?.(id, reason.trim());
      });
    },
    [show, requireReason, reason, waitForConfirm, closeAfterValidation, onConfirm],
  );
  return (
    <div
      ref={modalRef}
      className="modal modal-blur fade"
      id={`modal-${idModal}`}
      tabIndex="-1"
      role="dialog"
      aria-hidden="true">
      <div className={`modal-dialog ${requireReason ? "modal-md" : "modal-sm"} modal-dialog-centered`} role="document">
        <div
          className="modal-content  d-flex"
          style={{
            borderTop: `3px solid ${config.color}`,
          }}>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              if (!isConfirmCloseRef.current) {
                prepareClose(onCancel);
              }
            }}></button>

          <div className="modal-body text-center py-4">
            <div
              className="avatar avatar-xl rounded-circle my-3 mx-auto d-flex align-items-center justify-content-center"
              style={{ backgroundColor: config.bgIcon }}>
              {config.icon}
            </div>

            <h3 className="mb-1 fw-bolder">{title}</h3>
            <div className="text-secondary mb-3">{message}</div>

            {requireReason && (
              <div className="text-start mt-4">
                <label className="form-label fw-bold required" htmlFor={`${idModal}-reason`}>
                  {reasonLabel}
                </label>
                <textarea
                  id={`${idModal}-reason`}
                  className={`form-control rounded-4 ${error ? "is-invalid" : ""}`}
                  rows="3"
                  maxLength="500"
                  placeholder={reasonPlaceholder}
                  value={reason}
                  disabled={isSubmitting}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setError("");
                  }}
                />

                <div className="d-flex justify-content-between gap-3 mt-2">
                  <span className="form-hint">{reasonHint}</span>
                  <span className="form-hint flex-shrink-0">{reason.length}/500</span>
                </div>

                {error && (
                  <div key={error} className="invalid-feedback d-block animate-fade-up">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <div className="w-100">
              <div className="row">
                <div className="col-6">
                  <button
                    type="button"
                    className="btn btn-3 w-100"
                    data-bs-dismiss="modal"
                    disabled={isSubmitting}
                    onClick={() => prepareClose(onCancel)}>
                    {" "}
                    {cancelText}
                  </button>
                </div>
                <div className="col-6">
                  <button
                    type="button"
                    className="btn btn-primary w-100  text-white"
                    // style={{ backgroundColor: config.color }}
                    autoFocus
                    disabled={isSubmitting}
                    onClick={() => handleConfirm(idData)}>
                    {isSubmitting ? (
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    ) : type === "warning" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-check">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M5 12l5 5l10 -10" />
                      </svg>
                    ) : (
                      iconConfirm
                    )}

                    {isSubmitting ? "Memproses..." : confirmText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
